import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import AsyncSelect from "react-select/async";
import { components } from "react-select";
import { MotifFormField, MotifErrorMessage } from "@ey-xd/motif-react";

const DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 3;

const SingleValueChip = (props) => {
  const { data, selectProps } = props;

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    selectProps.onChange(null, { action: "clear", removedValues: [data] });
  };

  return (
    <components.SingleValue {...props}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          background: "#3a3a4a",
          borderRadius: "12px",
          border: "1px solid #747480",
          padding: "2px 8px 2px 10px",
          maxWidth: "100%",
          gap: "6px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <span
          title={data.value || data.label}
          style={{
            color: "#ffffff",
            fontSize: "13px",
            fontFamily: "EYInterstate, sans-serif",
            fontWeight: 300,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {data.label}
        </span>
        {!selectProps.isDisabled && (
          <span
            role="button"
            tabIndex={0}
            onMouseDown={handleClear}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleClear(e);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              color: "#a4a3b1",
              borderRadius: "0 12px 12px 0",
              padding: "0 2px",
            }}
            aria-label="Remove"
          >
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
              <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
            </svg>
          </span>
        )}
      </span>
    </components.SingleValue>
  );
};

const MultiPeoplePicker = ({
  getADUsers,
  onChange,
  options,
  disabledPicker,
  value,
  isErrorVisible,
  errorMessage,
  isErrorVisible2,
  errorMessage2,
  pageName,
  placeholder,
}) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    if (value) {
      setSelectedOptions(value);
    }
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const loadOptions = useCallback(
    (inputValue, callback) => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      if (!inputValue || inputValue.length < MIN_SEARCH_LENGTH) {
        callback([]);
        return;
      }

      debounceTimerRef.current = setTimeout(async () => {
        try {
          const data = await getADUsers(inputValue);
          if (Array.isArray(data)) {
            const formattedOptions = data.map((item) => ({
              id: item.id,
              label: item.emailId,
              value: item.displayName,
              firstName: item.firstName || "",
              lastName: item.lastName || "",
            }));
            callback(formattedOptions);
          } else {
            console.error("Expected an array but received:", data);
            callback([]);
          }
        } catch (error) {
          console.error("Error fetching users:", error);
          callback([]);
        }
      }, DEBOUNCE_MS);
    },
    [getADUsers],
  );

  const handleOnChange = (selectedList) => {
    setSelectedOptions(selectedList);
    onChange(selectedList);
  };

  const isMulti = pageName === "New Project";
  const hasValue = !isMulti && !!selectedOptions && !Array.isArray(selectedOptions);

  const formatOptionLabel = useCallback((option, { context }) => {
    if (context === "menu" && option.value && option.label !== option.value) {
      return (
        <div>
          <div style={{ fontWeight: 400, color: "#ffffff" }}>{option.value}</div>
          <div style={{ fontSize: "12px", color: "#a4a3b1" }}>{option.label}</div>
        </div>
      );
    }
    return option.value || option.label;
  }, []);

  return (
    <div>
      <div className="MultiselectDiv">
        <MotifFormField disabled={disabledPicker}>
          <div>
            {React.createElement(AsyncSelect, {
              cacheOptions: true,
              loadOptions,
              isDisabled: disabledPicker,
              placeholder:
                placeholder ||
                (isMulti
                  ? "Select project administrator"
                  : "Enter user email ID"),
              value: selectedOptions,
              onChange: handleOnChange,
              isMulti,
              closeMenuOnSelect: !isMulti,
              menuPosition: "fixed",
              isClearable: false,
              formatOptionLabel,
              isSearchable: !hasValue,
              ...(!isMulti && {
                components: { SingleValue: SingleValueChip },
              }),
              noOptionsMessage: ({ inputValue }) =>
                !inputValue || inputValue.length < MIN_SEARCH_LENGTH
                  ? `Type at least ${MIN_SEARCH_LENGTH} characters to search...`
                  : "No users found",
              options,
              styles: {
                control: (provided, state) => ({
                  ...provided,
                  background: "#23232f",
                  border: state.isFocused ? "1px solid #ffe600" : "1px solid #747480",
                  borderRadius: "4px",
                  color: "#ffffff",
                  minHeight: "44px",
                  boxShadow: "none",
                  "&:hover": {
                    borderColor: "#a4a3b1",
                  },
                }),
                menu: (provided) => ({
                  ...provided,
                  background: "#23232f",
                  border: "1px solid #747480",
                  borderRadius: "4px",
                  zIndex: 10,
                }),
                menuList: (provided) => ({
                  ...provided,
                  padding: 0,
                }),
                option: (provided, state) => ({
                  ...provided,
                  background: state.isFocused ? "#3a3a4a" : "transparent",
                  color: "#ffffff",
                  cursor: "pointer",
                  fontSize: "14px",
                  padding: "10px 12px",
                  "&:active": {
                    background: "#3a3a4a",
                  },
                }),
                singleValue: (provided) => ({
                  ...provided,
                  color: "#ffffff",
                  fontSize: "14px",
                }),
                multiValue: (provided) => ({
                  ...provided,
                  background: "#3a3a4a",
                  borderRadius: "4px",
                }),
                multiValueLabel: (provided) => ({
                  ...provided,
                  color: "#ffffff",
                  fontSize: "13px",
                }),
                multiValueRemove: (provided) => ({
                  ...provided,
                  color: "#a4a3b1",
                  "&:hover": {
                    background: "#747480",
                    color: "#ffffff",
                  },
                }),
                input: (provided) => ({
                  ...provided,
                  color: "#ffffff",
                  fontFamily: "EYInterstate, sans-serif",
                  fontWeight: 300,
                  fontSize: "14px",
                  lineHeight: "24px",
                }),
                placeholder: (provided) => ({
                  ...provided,
                  color: "#747480",
                  fontSize: "14px",
                }),
                indicatorSeparator: () => ({
                  display: "none",
                }),
                dropdownIndicator: (provided) => ({
                  ...provided,
                  ...(hasValue
                    ? { display: "none" }
                    : { color: "#a4a3b1", "&:hover": { color: "#ffffff" } }),
                }),
                clearIndicator: (provided) => ({
                  ...provided,
                  ...(hasValue
                    ? { display: "none" }
                    : { color: "#a4a3b1", "&:hover": { color: "#ffffff" } }),
                }),
                noOptionsMessage: (provided) => ({
                  ...provided,
                  color: "#747480",
                  fontSize: "14px",
                }),
                loadingMessage: (provided) => ({
                  ...provided,
                  color: "#747480",
                  fontSize: "14px",
                }),
              },
            })}
          </div>
          {isErrorVisible && (
            <MotifErrorMessage show={isErrorVisible}>
              {errorMessage}
            </MotifErrorMessage>
          )}
          {isErrorVisible2 && (
            <p
              show={isErrorVisible2}
              style={{
                fontSize: "0.857rem",
                color: "red",
                whiteSpace: "pre-wrap",
                overflowWrap: "break-word",
                marginTop: "0px",
                marginBottom: "0px",
                paddingLeft: "5%",
              }}
            >
              {errorMessage2}
            </p>
          )}
        </MotifFormField>
      </div>
    </div>
  );
};

MultiPeoplePicker.propTypes = {
  getADUsers: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  value: PropTypes.array,
  isErrorVisible: PropTypes.bool,
  errorMessage: PropTypes.string,
  isErrorVisible2: PropTypes.bool,
  errorMessage2: PropTypes.string,
  pageName: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
};

export default MultiPeoplePicker;